import APIUtils from '@spothero/utils/api';
import EnvironmentUtils from '@spothero/utils/environment';
import OptimizelyUtils from '@spothero/utils/optimizely';
import SegmentUtils from 'utils/segment';
import GTMUtils from 'utils/gtm';
import LuckyOrangeUtils from 'utils/lucky-orange';
import SentryUtils from '@spothero/utils/sentry';
import Config from 'config';

export const IMMEDIATE_FEATURES = {
    WEB_CANADA: 'web_canada',
};
// Define variables by feature in the format `[variableName, type]`
export const VARIABLES = {};
export const IMMEDIATE_AB_EXPERIMENTS = {
    AA_TEST: 'aa_test',
    MW_AA_TEST: 'mw_aa_test',
};

const isBrowser = EnvironmentUtils.isBrowser();

/**
 * Utilities for working with Optimizely.
 * @module ExperimentUtils
 */
const ExperimentUtils = {
    async init({
        isDev = false,
        optimizelyData = null,
        serverCookies = null,
        user = null,
        onLoad,
    } = {}) {
        const {datafile} = await OptimizelyUtils.initialize(optimizelyData, {
            isDev,
            isBrowser,
            serverCookies,
            isBot: Config.isBot,
            loadMechanism: APIUtils.get,
            onDatafileSuccess: this.clearVariationsCookie,
            onDatafileError: error => {
                SentryUtils.sendMessage(error);

                this.clearClient();
            },
        });

        this.initUserAttributes(user);

        if (onLoad) {
            onLoad();
        }

        return optimizelyData || datafile;
    },

    /**
     * Checks Optimizely to see if the user is bucketed in this experiment
     * Activates the experiment for the current session; an impression will be sent to Optimizely
     * Appends the experiment name and variation name to a cookie (used by analytics and the Reservations API)
     * @static
     * @function activate
     * @param {String} experimentName - Experiment name
     * @param {Object} options - Options to use
     * @param {Boolean} options.sendToAnalytics - Whether or not to send experiment information to Segment and GTM (defaults to true)
     * @example ExperimentUtils.activate('mw_checkout_value_props') => 'vp_below';
     * @example ExperimentUtils.activate('mw_checkout_value_props', {sendToAnalytics: false}) => 'vp_below';
     * @return {String} variationName - For each variation, returns the name of the variation assigned to this user, or 'a'
     */
    activate(experimentName, {sendToAnalytics = true} = {}) {
        if (!Config.isBot && !Config.runningTests) {
            const variationName = OptimizelyUtils.activate(experimentName);

            if (sendToAnalytics) {
                this.sendToAnalytics();
            }

            return variationName;
        }

        return 'a';
    },

    /**
     * Checks if a feature is enabled for a user
     * @static
     * @function isFeatureEnabled
     * @param {String} featureName - The name of the feature
     * @param {Object} options - Options to use
     * @param {Boolean} options.sendToAnalytics - Whether or not to send experiment information to Segment and GTM (defaults to true)
     * @example ExperimentUtils.isFeatureEnabled('web_canada');
     * @example ExperimentUtils.isFeatureEnabled('web_canada', {sendToAnalytics: true});
     * @returns {Boolean} isEnabled - Whether or not the feature is enabled for this user
     */
    isFeatureEnabled(featureName, {sendToAnalytics = true} = {}) {
        if (!Config.isBot && !Config.runningTests) {
            const isFeatureEnabled = OptimizelyUtils.isFeatureEnabled(
                featureName
            );

            if (isFeatureEnabled && sendToAnalytics) {
                this.sendToAnalytics();
            }

            return isFeatureEnabled;
        }

        return false;
    },

    /**
     * Checks Optimizely to see if the user is bucketed in this experiment
     * Does not activate the experiment for the current session; no impression will be sent to Optimizely
     * @static
     * @function getVariation
     * @param {String} experimentName - Name of experiment
     * @example ExperimentUtils.getVariation('homepage_tabbed_search') => 'tabbed_with_value_props';
     * @example ExperimentUtils.getVariation('homepage_tabbed_search', 'mw_reno_spot') => {
     *   homepage_tabbed_search: 'tabbed_with_value_props',
     *   mw_reno_spot: 'variation2',
     * };
     * @return {String|Object} variationName - For each variation, returns the name of the variation assigned to this user, or 'a'
     * If a single argument is passed, the variation name is returned as a string
     * If multiple arguments are passed, experiment name/variation name key/value pairs are returned
     */
    getVariation(experimentName) {
        if (!Config.isBot && !Config.runningTests) {
            return OptimizelyUtils.getVariation(experimentName);
        }

        return 'a';
    },

    /**
     * @static
     * @function getFeatureVariable
     * @param {Object} requiredParams - All params are required
     * @param {String} requiredParams.featureName - The name of the feature
     * @param {String} requiredParams.variableName - The name of the feature variable
     * @param {String} requiredParams.type - The type of the feature variable
     * @example ExperimentUtils.getFeatureVariable({
     *      featureName: 'web_canada',
     *      variableName: 'search_enabled',
     *      type: 'Boolean',
     * });
     * @returns {Double|Integer|String|Boolean} variableValue - The value of the given variable and type for the given feature
     */
    getFeatureVariable(requiredParams) {
        if (!Config.isBot && !Config.runningTests) {
            return OptimizelyUtils.getFeatureVariable(requiredParams);
        }

        return false;
    },

    /**
     * Sends experiment data to Segment and GTM
     * @static
     * @function sendToAnalytics
     * @returns {void}
     */
    sendToAnalytics() {
        if (Config.isDeployed && isBrowser) {
            const variationsCookie = OptimizelyUtils.getVariationsCookie();
            const luckyOrangeVariations = Object.keys(variationsCookie).reduce(
                (accm, key) => {
                    if (variationsCookie[key] !== 'a') {
                        accm[`experiment_${key}`] = variationsCookie[key];
                    }

                    return accm;
                },
                {}
            );

            SegmentUtils.addTraits({
                experiments: OptimizelyUtils.getSegmentExperiments(),
            });

            GTMUtils.push({
                event: 'experimentVariationsUpdated',
                experimentVariations: JSON.stringify(variationsCookie),
            });

            LuckyOrangeUtils.push(luckyOrangeVariations);
        }
    },

    /**
     * Initializes user attributes with device and environment data
     * @static
     * @function initUserAttributes
     * @param {Object} user - The user information to derive attributes from.
     * @returns {void}
     */
    initUserAttributes(user = {}) {
        const userAttributes = {
            client: Config.isMobile ? 'mobile-web' : 'web',
            environment: Config.isProduction ? 'production' : 'staging',
            email: user ? user.email : null,
            staff: Boolean(user && user.isAdmin).toString(),
            browser: EnvironmentUtils.getBrowserCategory(),
            ...(Config.requestIP && {
                ip: Config.requestIP,
            }),
        };

        OptimizelyUtils.setUserAttributes(userAttributes, {reset: true});
    },

    /**
     * A single location to bucket experiments and features needed on app load
     * Formats results for retrieval with redux (store.user)
     * @static
     * @function getBuckets
     * @param {Object} experimentObj - Dictionary of constant and Optimizely experiment name key/value pairs
     * @param {Object} featuresObj - Dictionary of constant and Optimizely feature flag name key/value pairs
     * @returns {Object} - Formatted experiment values for injecting into the redux (store.user)
     */
    getBuckets(
        experimentObj = IMMEDIATE_AB_EXPERIMENTS,
        featuresObj = IMMEDIATE_FEATURES
    ) {
        const experiments = Object.keys(experimentObj)
            .map(experimentName => experimentObj[experimentName])
            .reduce(
                (variations, experimentName) => ({
                    ...variations,
                    [experimentName]: this.activate(experimentName, {
                        sendToAnalytics: false,
                    }),
                }),
                {}
            );
        const features = Object.keys(featuresObj)
            .map(featureName => featuresObj[featureName])
            .reduce((enabledFeatures, featureName) => {
                const variables = VARIABLES[featureName];
                const isFeatureEnabled = this.isFeatureEnabled(featureName, {
                    sendToAnalytics: false,
                });

                return {
                    ...enabledFeatures,
                    [featureName]: {
                        isFeatureEnabled,
                        ...(variables &&
                            OptimizelyUtils.getEnabledFeatureVariables(
                                featureName,
                                variables
                            )),
                    },
                };
            }, {});

        return {
            ...experiments,
            ...features,
        };
    },
};

export default {
    ...OptimizelyUtils,
    ...ExperimentUtils,
};
